import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

// @material-ui
import Box from "@material-ui/core/Box";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Pagination from "@material-ui/lab/Pagination";
import { useHistory, useLocation } from "react-router-dom";

// components
import componentStylesGeneric from "assets/theme/views/admin/generic";
import TableBackdrop from "components/Backdrops/TableBackdrop/TableBackdrop";
import GenericButton from "components/Buttons/GenericButton";
import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import useQueryParams from "hooks/useQueryParams";
import useTokenHelper from "hooks/useTokenHelper";
import { getUserEmploymentHistoryAction } from "redux/actions/auth/getPersonalInformationAction";
import { labels } from "resources/resources";
import { PAGINATION } from "utils/const";
import AddEmploymentHistoryModal from "../Modals/AddEmploymentHistoryModal";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function EmploymentHistorySection(props) {
  const classes = useStylesGeneric();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const tokenHelper = useTokenHelper();

  const { getQueryParams, setQueryParams } = useQueryParams();
  const PAGE_INDEX_KEY = "eh_pageIndex";

  const [pageIndex, setPageIndex] = useState(() => {
    const params = getQueryParams();
    return params?.[PAGE_INDEX_KEY] || PAGINATION.FIRST_PAGE;
  });

  const [showAddEmploymentHistoryModal, setShowAddEmploymentHistoryModal] = useState({
    open: false,
    editing: false,
    data: {},
  });

  const handleChangePage = (event, value) => {
    setPageIndex(value);

    const params = getQueryParams();
    if (value > PAGINATION.FIRST_PAGE) {
      params[PAGE_INDEX_KEY] = value;
    } else {
      delete params[PAGE_INDEX_KEY];
    }
    setQueryParams(params, true);
  };

  useEffect(() => {
    const params = getQueryParams();
    const nextPageIndex = params?.[PAGE_INDEX_KEY] || PAGINATION.FIRST_PAGE;
    if (nextPageIndex !== pageIndex) {
      setPageIndex(nextPageIndex);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableHead = [
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_OpportunityNumber),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_Company),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_Profession),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_OpportunityType),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_WorkTimeFrame),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_Salary),
    intl.formatMessage(labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_Observations),
    "",
  ];

  const getUserEmploymentHistory = useCallback(
    (nextPageIndex) => {
      const filter = {
        pageIndex: nextPageIndex,
        pageSize: PAGINATION.PAGE_SIZE,
        userId: tokenHelper.getUserId(),
      };

      props.getUserEmploymentHistory(filter);
    },
    [props.getUserEmploymentHistory, tokenHelper]
  );

  useEffect(() => {
    getUserEmploymentHistory(pageIndex);
  }, [getUserEmploymentHistory, pageIndex]);

  function handleToggleAddEmploymentHistoryModal(data = {}, editing = false) {
    setShowAddEmploymentHistoryModal((prevState) => ({
      open: !prevState.open,
      editing,
      data,
    }));
  }

  function handleOnSuccessAction() {
    setPageIndex(PAGINATION.FIRST_PAGE);

    const params = getQueryParams();
    delete params[PAGE_INDEX_KEY];
    setQueryParams(params, true);

    getUserEmploymentHistory(PAGINATION.FIRST_PAGE);
  }

  return (
    <>
      <CardTableContainer
        title={intl.formatMessage(
          labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_EmploymentHistory
        )}
        count={props.authState?.getUserEmploymentHistory_result?.totalCount}
        responsiveHeader={isSmDown}
        actionButtonLabel={intl.formatMessage(
          labels.Label_UserProfile_UserSingular_EmploymentAndUnemploymentHistory_AddRecord
        )}
        actionButtonSize={isSmDown ? "medium" : "large"}
        actionButtonOnClick={() => handleToggleAddEmploymentHistoryModal()}
      >
        <TableContainer>
          <Box component={Table} alignItems="center" marginBottom="0!important">
            <TableHead>
              <TableRow>
                {tableHead.map((prop, key) => (
                  <TableCell
                    key={key}
                    classes={{
                      root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                    }}
                  >
                    {prop}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {props.authState?.getUserEmploymentHistory_result?.data?.map((prop, key) => (
                <TableRow key={key}>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    {prop?.opportunityReference && (
                      <GenericButton
                        color="ghost"
                        typeSubmit={false}
                        size="small"
                        onClick={() => {
                          history.push("/admin/available-opportunity-details/" + prop?.opportunityAlternateKey);
                        }}
                      >
                        {prop?.opportunityReference}
                      </GenericButton>
                    )}

                    {!prop?.opportunityReference && <Box>N/A</Box>}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.companyName}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.professionalCategoryDescription}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.opportunityTypeDescription}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>
                      {!prop?.workedSince && !prop?.workedUntil
                        ? intl.formatMessage(labels.Label_Undefined)
                        : (prop?.workedSince
                            ? moment(prop?.workedSince).format("DD/MM/YYYY")
                            : intl.formatMessage(labels.Label_Undefined)) +
                          " - " +
                          (prop?.workedUntil
                            ? moment(prop?.workedUntil).format("DD/MM/YYYY")
                            : intl.formatMessage(labels.Label_Undefined))}
                    </Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.salary}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.observations}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <GenericButton
                      typeSubmit={false}
                      color="tertiary"
                      size="small"
                      onClick={() => {
                        handleToggleAddEmploymentHistoryModal(prop, true);
                      }}
                    >
                      {intl.formatMessage(labels.Label_Button_Edit)}
                    </GenericButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Box>
          <TableBackdrop open={props?.authState?.getUserEmploymentHistory_loading} />
        </TableContainer>

        <Pagination
          className={classes.alignPagination}
          count={props.authState?.getUserEmploymentHistory_result?.totalPages}
          page={pageIndex}
          onChange={handleChangePage}
          color="primary"
        ></Pagination>
      </CardTableContainer>

      <GenericDialog
        maxWidth={"md"}
        open={showAddEmploymentHistoryModal.open}
        onClose={handleToggleAddEmploymentHistoryModal}
      >
        <AddEmploymentHistoryModal
          data={showAddEmploymentHistoryModal.data}
          onCloseModal={handleToggleAddEmploymentHistoryModal}
          editing={showAddEmploymentHistoryModal.editing}
          onSuccess={handleOnSuccessAction}
        />
      </GenericDialog>
    </>
  );
}
const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getUserEmploymentHistory: (data) => dispatch(getUserEmploymentHistoryAction(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmploymentHistorySection);
